import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  collection, doc, setDoc, updateDoc, deleteDoc,
  onSnapshot, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase.js';
import { COL } from '@bytecore/firestore-schema';

// Each cart ID is also the Firestore pos_carts/{id} doc ID.
let _nextSeq = 1;
function makeCartId() {
  return `cart-${Date.now()}-${_nextSeq++}-${Math.random().toString(36).slice(2, 6)}`;
}

function emptyCart(overrides = {}) {
  return {
    id:               overrides.id ?? makeCartId(),
    guestName:        null,
    client:           null,
    items:            [],
    coupon:           null,
    giftCards:        [],
    payments:         [],
    fiscalData:       null,
    elderlyDiscount:  null,
    serviceCharge:    null,
    createdAt:        Date.now(),
    lastActiveAt:     Date.now(),
    ...overrides,
  };
}

// ── Firestore helpers ─────────────────────────────────────────────────────────

// Track which cart IDs have been written to Firestore at least once.
const _synced = new Set();
// Debounce timers — cartId → timer handle.
const _timers = {};

const DEBOUNCE = 800; // ms between mutations and Firestore write

function _schedule(cartId, getCarts) {
  clearTimeout(_timers[cartId]);
  _timers[cartId] = setTimeout(() => {
    delete _timers[cartId];
    _flush(cartId, getCarts());
  }, DEBOUNCE);
}

async function _flush(cartId, cartsMap) {
  const cart = cartsMap.get(cartId);
  if (!cart) return;

  if (!cart.items.length) {
    // Cart became empty — remove from Firestore if it was ever written.
    if (_synced.has(cartId)) {
      _synced.delete(cartId);
      try {
        await deleteDoc(doc(db, COL.POS_CARTS, cartId));
      } catch (e) {
        console.warn('[carts] delete failed:', e.code);
      }
    }
    return;
  }

  const payload = {
    guestName:       cart.guestName ?? null,
    clientId:        cart.client?.id ?? null,
    clientName:      cart.client?.name ?? null,
    items:           cart.items,
    coupon:          cart.coupon ?? null,
    giftCards:       cart.giftCards ?? [],
    payments:        cart.payments ?? [],
    fiscalData:      cart.fiscalData ?? null,
    elderlyDiscount: cart.elderlyDiscount ?? null,
    serviceCharge:   cart.serviceCharge ?? null,
    updatedAt:       serverTimestamp(),
  };

  try {
    if (!_synced.has(cartId)) {
      payload.createdAt = serverTimestamp();
      await setDoc(doc(db, COL.POS_CARTS, cartId), payload);
      _synced.add(cartId);
    } else {
      await updateDoc(doc(db, COL.POS_CARTS, cartId), payload);
    }
  } catch (e) {
    console.warn('[carts] persist failed:', e.code);
  }
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useCartsStore = defineStore('pos-carts', () => {
  const carts        = ref(new Map());
  const activeCartId = ref(null);
  let _unsubCarts    = null;
  let _initialised   = false;

  // ── Derived ──────────────────────────────────────────────────────────────────

  const activeCart = computed(() =>
    activeCartId.value ? (carts.value.get(activeCartId.value) ?? null) : null,
  );

  const recentCarts = computed(() =>
    [...carts.value.values()]
      .sort((a, b) => b.lastActiveAt - a.lastActiveAt)
      .slice(0, 5),
  );

  const allCarts = computed(() =>
    [...carts.value.values()].sort((a, b) => b.lastActiveAt - a.lastActiveAt),
  );

  // ── Init: start Firestore listener + create fallback empty cart ───────────────

  function init() {
    if (_initialised) return;
    _initialised = true;

    // Ensure there's always at least one cart in the UI immediately.
    if (carts.value.size === 0) {
      const placeholder = emptyCart();
      carts.value.set(placeholder.id, placeholder);
      activeCartId.value = placeholder.id;
    }

    // Subscribe to all open carts from Firestore (real-time, cross-device).
    _unsubCarts?.();
    _unsubCarts = onSnapshot(
      collection(db, COL.POS_CARTS),
      (snap) => {
        let removedPlaceholder = false;

        snap.docChanges().forEach(change => {
          const d    = change.doc;
          const id   = d.id;
          const data = d.data();

          if (change.type === 'added') {
            // Don't overwrite a cart we're actively editing (pending local write).
            if (carts.value.has(id) && _synced.has(id)) return;

            const cart = emptyCart({
              id,
              guestName:       data.guestName ?? null,
              client:          data.clientId ? { id: data.clientId, name: data.clientName } : null,
              items:           data.items ?? [],
              coupon:          data.coupon ?? null,
              giftCards:       data.giftCards ?? [],
              payments:        data.payments ?? [],
              fiscalData:      data.fiscalData ?? null,
              elderlyDiscount: data.elderlyDiscount ?? null,
              serviceCharge:   data.serviceCharge ?? null,
              createdAt:       data.createdAt?.toMillis() ?? Date.now(),
              lastActiveAt:    data.updatedAt?.toMillis() ?? Date.now(),
            });
            carts.value.set(id, cart);
            _synced.add(id);

            // Remove the empty placeholder cart that was created before load.
            if (!removedPlaceholder) {
              for (const [pid, pc] of carts.value) {
                if (pid !== id && !pc.items.length && !_synced.has(pid)) {
                  if (activeCartId.value === pid) activeCartId.value = id;
                  carts.value.delete(pid);
                  removedPlaceholder = true;
                  break;
                }
              }
            }

            if (!activeCartId.value || !carts.value.has(activeCartId.value)) {
              activeCartId.value = id;
            }
          }

          if (change.type === 'modified') {
            // Only apply remote updates when we don't have a pending local write.
            if (_timers[id]) return;
            const cart = carts.value.get(id);
            if (!cart) return;
            cart.items           = data.items ?? cart.items;
            cart.guestName       = data.guestName ?? cart.guestName;
            cart.client          = data.clientId ? { id: data.clientId, name: data.clientName } : cart.client;
            cart.coupon          = data.coupon ?? cart.coupon;
            cart.serviceCharge   = data.serviceCharge ?? cart.serviceCharge;
            cart.fiscalData      = data.fiscalData ?? cart.fiscalData;
            cart.elderlyDiscount = data.elderlyDiscount ?? cart.elderlyDiscount;
            cart.lastActiveAt    = data.updatedAt?.toMillis() ?? cart.lastActiveAt;
          }

          if (change.type === 'removed') {
            _synced.delete(id);
            if (!carts.value.has(id)) return;
            if (activeCartId.value === id) {
              const next = [...carts.value.keys()].find(k => k !== id);
              if (next) {
                activeCartId.value = next;
              } else {
                const fresh = emptyCart();
                carts.value.set(fresh.id, fresh);
                activeCartId.value = fresh.id;
              }
            }
            carts.value.delete(id);
          }
        });
      },
      (err) => {
        console.warn('[carts] snapshot error:', err.code);
      },
    );
  }

  function unsubscribe() {
    _unsubCarts?.();
    _unsubCarts = null;
    _initialised = false;
  }

  // ── Cart lifecycle ────────────────────────────────────────────────────────────

  function newCart(overrides = {}) {
    const cart = emptyCart(overrides);
    carts.value.set(cart.id, cart);
    activeCartId.value = cart.id;
    return cart;
  }

  function switchToCart(cartId) {
    if (!carts.value.has(cartId)) return;
    activeCartId.value = cartId;
    _touch(cartId);
  }

  function closeCart(cartId) {
    // Cancel any pending write and remove from Firestore.
    clearTimeout(_timers[cartId]);
    delete _timers[cartId];
    if (_synced.has(cartId)) {
      _synced.delete(cartId);
      deleteDoc(doc(db, COL.POS_CARTS, cartId)).catch(() => {});
    }

    carts.value.delete(cartId);

    if (activeCartId.value === cartId) {
      const next = [...carts.value.keys()][0];
      if (next) {
        activeCartId.value = next;
      } else {
        const fresh = emptyCart();
        carts.value.set(fresh.id, fresh);
        activeCartId.value = fresh.id;
      }
    }
  }

  function _touch(cartId) {
    const cart = carts.value.get(cartId);
    if (cart) cart.lastActiveAt = Date.now();
  }

  function _mutated(cartId) {
    _touch(cartId);
    _schedule(cartId, () => carts.value);
  }

  // ── Item operations ───────────────────────────────────────────────────────────

  function addItem(cartId, item) {
    const cart = carts.value.get(cartId ?? activeCartId.value);
    if (!cart) return;
    const existing = cart.items.find(i => i.productId === item.productId && !item.modifiers?.length);
    if (existing) {
      existing.qty      += item.qty ?? 1;
      existing.lineTotal = (existing.unitPrice + _modDelta(existing)) * existing.qty;
    } else {
      cart.items.push({ ...item, qty: item.qty ?? 1 });
    }
    _mutated(cart.id);
  }

  function updateQty(cartId, productId, qty) {
    const cart = carts.value.get(cartId ?? activeCartId.value);
    if (!cart) return;
    if (qty <= 0) {
      cart.items = cart.items.filter(i => i.productId !== productId);
    } else {
      const item = cart.items.find(i => i.productId === productId);
      if (item) {
        item.qty       = qty;
        item.lineTotal = (item.unitPrice + _modDelta(item)) * qty;
      }
    }
    _mutated(cart.id);
  }

  function removeItem(cartId, productId) {
    const cart = carts.value.get(cartId ?? activeCartId.value);
    if (!cart) return;
    cart.items = cart.items.filter(i => i.productId !== productId);
    _mutated(cart.id);
  }

  function _modDelta(item) {
    return (item.modifiers ?? []).reduce((s, m) => s + (m.priceDelta ?? 0), 0);
  }

  // ── Cart metadata ─────────────────────────────────────────────────────────────

  function setClient(cartId, client) {
    const cart = carts.value.get(cartId ?? activeCartId.value);
    if (!cart) return;
    cart.client = client;
    cart.guestName = null;
    _mutated(cart.id);
  }

  function setGuestName(cartId, name) {
    const cart = carts.value.get(cartId ?? activeCartId.value);
    if (!cart) return;
    cart.guestName = name;
    _mutated(cart.id);
  }

  function setCoupon(cartId, coupon) {
    const cart = carts.value.get(cartId ?? activeCartId.value);
    if (!cart) return;
    cart.coupon = coupon;
    _mutated(cart.id);
  }

  function setFiscalData(cartId, data) {
    const cart = carts.value.get(cartId ?? activeCartId.value);
    if (!cart) return;
    cart.fiscalData = data;
    _mutated(cart.id);
  }

  function setElderlyDiscount(cartId, data) {
    const cart = carts.value.get(cartId ?? activeCartId.value);
    if (!cart) return;
    cart.elderlyDiscount = data;
    _mutated(cart.id);
  }

  function setServiceCharge(cartId, charge) {
    const cart = carts.value.get(cartId ?? activeCartId.value);
    if (!cart) return;
    cart.serviceCharge = charge;
    _mutated(cart.id);
  }

  // ── Totals ────────────────────────────────────────────────────────────────────

  function totals(cartId) {
    const cart = carts.value.get(cartId ?? activeCartId.value);
    if (!cart) return { subtotal: 0, membershipDiscount: 0, couponDiscount: 0, elderlyDiscount: 0, discount: 0, serviceCharge: 0, total: 0 };

    const subtotal = cart.items.reduce((s, i) => s + i.lineTotal, 0);

    const membershipPct = cart.client?.membership?.foodDiscountPct ?? 0;
    const membershipDiscount = cart.items.reduce((s, i) => {
      if (i.discountExcluded || i.type === 'serviceCharge') return s;
      return s + Math.round(i.lineTotal * (membershipPct / 100));
    }, 0);

    const couponDiscount = cart.coupon ? _couponDiscount(cart) : 0;

    const elderlyPct = cart.elderlyDiscount?.appliedPct ?? 0;
    const elderlyDiscount = elderlyPct > 0
      ? Math.round((subtotal - membershipDiscount - couponDiscount) * (elderlyPct / 100))
      : 0;

    const discountedSubtotal = subtotal - membershipDiscount - couponDiscount - elderlyDiscount;

    const serviceChargeAmt = cart.serviceCharge
      ? (cart.serviceCharge.mode === 'percent'
          ? Math.round(discountedSubtotal * (cart.serviceCharge.value / 100))
          : cart.serviceCharge.value)
      : 0;

    return {
      subtotal,
      membershipDiscount,
      couponDiscount,
      elderlyDiscount,
      discount: membershipDiscount + couponDiscount + elderlyDiscount,
      serviceCharge: serviceChargeAmt,
      total: discountedSubtotal + serviceChargeAmt,
    };
  }

  function _couponDiscount(cart) {
    const coupon = cart.coupon;
    if (!coupon) return 0;
    const eligible = cart.items.filter(item => {
      if (coupon.scope === 'all') return true;
      if (coupon.scope === 'categories') return coupon.scopeIds?.includes(item.categoryId);
      if (coupon.scope === 'products') return coupon.scopeIds?.includes(item.productId);
      return false;
    });
    if (!eligible.length) return 0;
    if (coupon.singleItem) {
      const cheapest = [...eligible].sort((a, b) => a.unitPrice - b.unitPrice)[0];
      return coupon.type === 'percent'
        ? Math.min(Math.round(cheapest.unitPrice * (coupon.value / 100)), coupon.maxDiscountAmount ?? Infinity)
        : Math.min(coupon.value, cheapest.unitPrice);
    }
    const sub = eligible.reduce((s, i) => s + i.lineTotal, 0);
    return coupon.type === 'percent'
      ? Math.min(Math.round(sub * (coupon.value / 100)), coupon.maxDiscountAmount ?? Infinity)
      : Math.min(coupon.value, sub);
  }

  return {
    carts, activeCartId, activeCart, recentCarts, allCarts,
    init, unsubscribe,
    newCart, switchToCart, closeCart,
    addItem, updateQty, removeItem,
    setClient, setGuestName, setCoupon, setFiscalData,
    setElderlyDiscount, setServiceCharge,
    totals,
  };
});
